import { Link } from 'react-router-dom';
import { MapPin, Cable, Wifi } from 'lucide-react';

interface LocationLinksProps {
  currentLocation?: string;
  service?: string;
  className?: string;
}

const LOCATIONS = [
  { name: 'Boise', slug: 'boise' },
  { name: 'Meridian', slug: 'meridian' },
  { name: 'Nampa', slug: 'nampa' },
  { name: 'Caldwell', slug: 'caldwell' },
  { name: 'Eagle', slug: 'eagle' },
  { name: 'Kuna', slug: 'kuna' },
  { name: 'Star', slug: 'star' },
  { name: 'Garden City', slug: 'garden-city' },
  { name: 'Middleton', slug: 'middleton' },
];

export function LocationLinks({ currentLocation, service, className = "" }: LocationLinksProps) {
  const getLocationHref = (locationSlug: string) => {
    if (service === 'network-cabling') {
      return `/location/${locationSlug}/network-cabling`;
    }
    if (service === 'unifi-installer') {
      return `/location/${locationSlug}/unifi-installer`;
    }
    return `/location/${locationSlug}`;
  };

  const getLocationTitle = (locationName: string) => {
    if (service === 'network-cabling') {
      return `${locationName} Network Cabling`;
    }
    if (service === 'unifi-installer') {
      return `UniFi Installation in ${locationName}`;
    }
    return `IT Services in ${locationName}`;
  };

  const serviceLabel = service === 'network-cabling'
    ? 'Network Cabling Services'
    : service === 'unifi-installer'
      ? 'UniFi Installation Services'
      : 'IT Services';

  const serviceDesc = service === 'network-cabling'
    ? 'network cabling and structured cabling'
    : service === 'unifi-installer'
      ? 'UniFi network installation and configuration'
      : 'IT support and technology solutions';

  return (
    <section className={`py-12 bg-gray-50 dark:bg-gray-900 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
            {serviceLabel} Throughout the Treasure Valley
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Professional {serviceDesc} for businesses across Idaho.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
          {LOCATIONS.map((location) => (
            <Link
              key={location.slug}
              to={getLocationHref(location.slug)}
              className={`group flex items-center p-4 rounded-lg transition-all duration-300 hover:shadow-md ${
                currentLocation === location.slug
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-900 dark:text-primary-100'
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title={getLocationTitle(location.name)}
            >
              <MapPin className="w-4 h-4 mr-2 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium">{location.name}</span>
            </Link>
          ))}
        </div>

        {/* Sub-service links — only when not already on a specific service */}
        {!service && (
          <div className="mt-8 max-w-4xl mx-auto space-y-4">
            <div className="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm">
              <span className="flex items-center font-semibold text-gray-700 dark:text-gray-200 mr-2">
                <Cable className="w-4 h-4 mr-1 text-primary-600" />
                Network Cabling:
              </span>
              {LOCATIONS.map((loc, i) => (
                <span key={loc.slug}>
                  <Link
                    to={`/location/${loc.slug}/network-cabling`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                    title={`Network Cabling in ${loc.name}, Idaho`}
                  >
                    {loc.name}
                  </Link>
                  {i < LOCATIONS.length - 1 && <span className="text-gray-400 mx-1">&middot;</span>}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm">
              <span className="flex items-center font-semibold text-gray-700 dark:text-gray-200 mr-2">
                <Wifi className="w-4 h-4 mr-1 text-primary-600" />
                UniFi Installation:
              </span>
              {LOCATIONS.map((loc, i) => (
                <span key={loc.slug}>
                  <Link
                    to={`/location/${loc.slug}/unifi-installer`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                    title={`UniFi Installation in ${loc.name}, Idaho`}
                  >
                    {loc.name}
                  </Link>
                  {i < LOCATIONS.length - 1 && <span className="text-gray-400 mx-1">&middot;</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Serving businesses throughout Ada County, Canyon County, and Gem County
          </p>
        </div>
      </div>
    </section>
  );
}